import { Controller } from "@hotwired/stimulus"
import { badgeClasses } from '@/utils/badge_classes'

export default class extends Controller {
  static targets = ["field"]
  static values = {
    datasetId: Number,
    field: String,
    currentValue: String
  }

  connect() {
    this.isEditing = false
    this.element.addEventListener('click', this.edit.bind(this))
  }

  edit(event) {
    event.stopPropagation() // Prevent row click navigation

    // Prevent multiple edit sessions
    if (this.isEditing) {
      return
    }

    this.isEditing = true
    const currentElement = this.fieldTarget
    const currentText = currentElement.textContent.trim()

    // Create appropriate input based on field type
    let inputElement

    if (this.fieldValue === 'purpose') {
      inputElement = this.createSelect([
        { value: 'fine_tune', text: 'Fine-tune' },
        { value: 'input_output', text: 'Input/Output' }
      ])
    } else if (this.fieldValue === 'status') {
      inputElement = this.createSelect([
        { value: 'draft', text: 'Draft' },
        { value: 'validating', text: 'Validating' },
        { value: 'validated', text: 'Validated' },
        { value: 'exported', text: 'Exported' }
      ])
    } else {
      inputElement = this.createTextInput()
    }

    // Replace the element
    currentElement.style.display = 'none'
    currentElement.parentNode.appendChild(inputElement)
    inputElement.focus()

    // Handle save/cancel
    // Use a timeout for blur to allow dropdown selection to complete
    inputElement.addEventListener('blur', (e) => {
      setTimeout(() => {
        // Only save if the input still exists (wasn't already saved/cancelled)
        if (inputElement.parentNode) {
          this.save(inputElement, currentElement)
        }
      }, 150)
    })

    inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.save(inputElement, currentElement)
      } else if (e.key === 'Escape') {
        this.cancel(inputElement, currentElement)
      }
    })

    // For select elements, save immediately on change
    if (inputElement.tagName === 'SELECT') {
      inputElement.addEventListener('change', () => {
        this.save(inputElement, currentElement)
      })
    }
  }

  createTextInput() {
    const input = document.createElement('input')
    input.type = 'text'
    input.value = this.currentValueValue
    input.className = 'form-control form-control-sm'
    input.style.width = 'auto'
    input.style.minWidth = '120px'
    return input
  }

  createSelect(options) {
    const select = document.createElement('select')
    select.className = 'form-select form-select-sm'
    select.style.width = 'auto'
    select.style.minWidth = '120px'

    options.forEach(option => {
      const optionElement = document.createElement('option')
      optionElement.value = option.value
      optionElement.textContent = option.text
      optionElement.selected = option.value === this.currentValueValue
      select.appendChild(optionElement)
    })

    return select
  }

  async save(inputElement, originalElement) {
    const newValue = inputElement.value

    if (newValue === this.currentValueValue) {
      this.cancel(inputElement, originalElement)
      return
    }

    try {
      const response = await fetch(`/settings/ai/datasets/${this.datasetIdValue}/inline_update`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          field: this.fieldValue,
          value: newValue
        })
      })

      const result = await response.json()

      if (result.success) {
        // Update the display
        if (this.fieldValue === 'status') {
          // Update badge
          originalElement.className = badgeClasses({ variant: result.badge_variant })
          originalElement.textContent = result.display_value
        } else {
          originalElement.textContent = result.display_value
        }

        // Update the current value for future edits
        this.currentValueValue = result.value

        // Remove input and show original
        inputElement.remove()
        originalElement.style.display = ''
        this.isEditing = false
      } else {
        alert(`Error: ${result.message}`)
        this.cancel(inputElement, originalElement)
      }
    } catch (error) {
      console.error('Error updating field:', error)
      alert('Error updating field. Please try again.')
      this.cancel(inputElement, originalElement)
    }
  }

  cancel(inputElement, originalElement) {
    inputElement.remove()
    originalElement.style.display = ''
    this.isEditing = false
  }
} 